import { useEffect, useRef, useState } from "react";
import "./CustomFeatureModal.scss";
import getDataFromApi from "../../../../components/getDataFromApi";
import {
    applyCategoryChange,
    applySpecFieldChange,
    applyUnitChange,
    buildResourceFormDefaults,
    buildResourcePayload,
    coerceResourceFieldValue,
    getCategoryFieldKey,
    getFieldControl,
    getSchemaFieldTitle,
    getSpecFields,
    getUnitOptions,
    isNestedDiscriminatorField,
    orderSpecEntries,
    prettyFieldLabel,
    shouldShowUnit,
} from "./resourceFormSchema";

const extractApiErrorMessage = (error) => {
    const candidates = [
        error?.body?.message,
        error?.body?.detail,
        error?.response?.data?.message,
        error?.response?.data?.detail,
        error?.detail,
        error?.message,
    ];

    for (const candidate of candidates) {
        if (!candidate) continue;

        if (typeof candidate === "object") {
            const nestedMessage = candidate.message || candidate.detail;
            if (nestedMessage) return String(nestedMessage);
            continue;
        }

        const rawMessage = String(candidate).replace(/^\d{3}:\s*/, "");
        const jsonStart = rawMessage.indexOf("{");

        if (jsonStart !== -1) {
            try {
                const parsed = JSON.parse(rawMessage.substring(jsonStart));
                const parsedMessage = parsed?.message || parsed?.detail;
                if (parsedMessage) return String(parsedMessage);
            } catch { }
        }

        if (rawMessage.trim()) return rawMessage.trim();
    }

    return "";
};

const isPlanAssignmentError = (message) => {
    const lowerMessage = String(message || "").toLowerCase();

    return (
        lowerMessage.includes("is used in") ||
        lowerMessage.includes("assigned to") ||
        lowerMessage.includes("used in plan") ||
        lowerMessage.includes("used by plan") ||
        lowerMessage.includes("active plan") ||
        lowerMessage.includes("locked because") ||
        lowerMessage.includes("modifications are disabled")
    );
};

const getPlanAssignmentEditMessage = (itemType) =>
    `This ${itemType} is assigned to one or more active plans or templates. Remove it from those plans or templates before editing it.`;

const throwIfPlanAssignmentResponse = (response) => {
    const apiMessage = extractApiErrorMessage(response);

    if (!isPlanAssignmentError(apiMessage)) return;

    const error = new Error(apiMessage);
    error.type = "EDIT_BLOCKED";
    throw error;
};

export default function CustomResourceModal({
    showModal,
    onClose,
    entitlementId,
    onSubmit,
    editData,
    isEdit,
}) {
    const [schema, setSchema] = useState(null);
    const [uiOrder, setUiOrder] = useState([]);
    const [formData, setFormData] = useState({});
    const [initialFormData, setInitialFormData] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [editBlocked, setEditBlocked] = useState(false);
    const [errors, setErrors] = useState({});

    const lastEntitlementRef = useRef(null);

    useEffect(() => {
        if (!showModal) return;

        const entitlementChanged =
            lastEntitlementRef.current !== null &&
            String(lastEntitlementRef.current) !== String(entitlementId);

        if (schema && !entitlementChanged) return;

        setFormLoading(true);

        getDataFromApi(`${window.ENV.API_URL}/forms/admin/create_resource`, "get")
            .then((result) => {
                const jsonSchema = result.json_schema;
                setSchema(jsonSchema);
                setUiOrder(result.ui_schema?.["ui:order"] || []);
                lastEntitlementRef.current = entitlementId;
                setFormLoading(false);
            })
            .catch((e) => {
                console.error(e.message);
                setFormLoading(false);
            });
    }, [showModal, schema, entitlementId]);

    useEffect(() => {
        if (!showModal || !schema) return;

        const defaults = buildResourceFormDefaults(schema, isEdit ? editData : null);

        setFormData(defaults);
        setInitialFormData(JSON.parse(JSON.stringify(defaults)));
        setErrors({});
        setSubmitError(null);
        setEditBlocked(false);
    }, [showModal, schema, entitlementId, isEdit, editData]);

    const update = (key, value) => {
        const nextValue = coerceResourceFieldValue(key, value);

        setFormData((prev) => {
            if (key === getCategoryFieldKey(schema)) {
                return applyCategoryChange(prev, schema, nextValue);
            }
            if (key === "Unit") {
                return applyUnitChange(prev, schema, nextValue);
            }
            if (isNestedDiscriminatorField(schema, prev, key)) {
                return applySpecFieldChange(prev, schema, key, nextValue);
            }
            return { ...prev, [key]: nextValue };
        });
    };

    const handleZeroOnFocus = (e) => {
        if (String(e.target.value) === "0") {
            e.target.select();
        }
    };

    const clearFieldError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: false }));
        if (!editBlocked) {
            setSubmitError(null);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let message = "";

        if (!String(formData["Resource Name"] || "").trim()) {
            newErrors["Resource Name"] = true;
            message = "Please enter resource name";
        }

        const categoryKey = getCategoryFieldKey(schema);
        if (!String(formData[categoryKey] || "").trim()) {
            newErrors[categoryKey] = true;
            message = message || "Please select a category";
        }

        if (shouldShowUnit(schema, formData) && !String(formData.Unit || "").trim()) {
            newErrors.Unit = true;
            message = message || "Please select a unit";
        }

        const unitPrice =
            formData["Unit Price"] === "" ? 0 : Number(formData["Unit Price"] || 0);
        const min =
            formData.min_value === "" ? 0 : Number(formData.min_value || 0);
        const max =
            formData.max_value === "" ? 0 : Number(formData.max_value || 0);

        if (unitPrice < 0) {
            newErrors["Unit Price"] = true;
            message = "Unit Price cannot be negative.";
        }

        if (min < 0) {
            newErrors["min_value"] = true;
            message = "Minimum value cannot be negative.";
        }

        if (max < 0) {
            newErrors["max_value"] = true;
            message = "Maximum value cannot be negative.";
        }

        if (min > 0 && max > 0 && max < min) {
            newErrors["max_value"] = true;
            message = "Maximum value cannot be less than Minimum value.";
        }

        setErrors(newErrors);
        setSubmitError(message || null);

        return Object.keys(newErrors).length === 0;
    };

    const normalizeForCompare = (data) => buildResourcePayload(data, schema);

    const hasResourceChanges = () => {
        if (!isEdit || !initialFormData) return true;

        const current = normalizeForCompare(formData);
        const initial = normalizeForCompare(initialFormData);

        return JSON.stringify(current) !== JSON.stringify(initial);
    };

    const warningMessage =
        "The Unit Price and Minimum Value are currently set to 0. Please confirm that you intend to create a resource with no pricing and no minimum allocation by clicking Submit again.";


    const handleSubmit = () => {
        if (!validateForm()) return;

        if (isEdit && initialFormData && !hasResourceChanges()) {
            setSubmitError("No changes detected.");
            return;
        }

        const payload = buildResourcePayload(formData, schema);
        const unitPrice = Number(payload["Unit Price"] || 0);
        const min = Number(payload.min_value || 0);

        if (
            !isEdit &&
            unitPrice === 0 &&
            min === 0 &&
            submitError !== warningMessage
        ) {
            setSubmitError(warningMessage);
            return;
        }

        setSubmitLoading(true);
        setSubmitError(null);

        const apiUrl =
            isEdit && editData?.id
                ? `${window.ENV.API_URL}/forms/admin/update_resource/${editData.id}`
                : `${window.ENV.API_URL}/forms/admin/create_resource`;

        const method = "post";

        getDataFromApi(apiUrl, method, payload)
            .then((response) => {
                if (isEdit) {
                    throwIfPlanAssignmentResponse(response);
                }

                setSubmitLoading(false);
                onClose?.();
                onSubmit?.();
            })
            .catch((e) => {
                setSubmitLoading(false);

                const apiMessage = extractApiErrorMessage(e);
                let message = apiMessage || "Failed to submit request. Please try again.";

                if (isEdit && isPlanAssignmentError(apiMessage)) {
                    setEditBlocked(true);
                    message = apiMessage || getPlanAssignmentEditMessage("resource");
                }

                setSubmitError(message);
            });
    };

    if (!showModal) return null;

    if (formLoading) {
        return (
            <div className="center-form">
                <div >Loading…</div>
            </div>
        );
    }

    if (!schema) return null;

    const categoryKey = getCategoryFieldKey(schema);
    const categoryOptions = schema.properties[categoryKey] || {};
    const selectedCategory = formData[categoryKey];
    const unitOptions = getUnitOptions(schema, formData);
    const showUnit = shouldShowUnit(schema, formData);
    const specFields = getSpecFields(schema, formData);
    const specEntries = orderSpecEntries(specFields, uiOrder);
    const isUnchanged = isEdit && initialFormData && !hasResourceChanges();
    const isUpdateBlocked = isEdit && editBlocked;

    return (
        <>
            <div className="center-form">
                <div >
                    <div className="cf-head">
                        <div>
                            {/* <span className="cf-tag text-primary">
                                {isEdit ? "Edit global resource" : "Create global resource"}
                            </span> */}
                            <h2>
                                {isEdit ? "Edit" : "New"}{" "}
                                <strong className="text-primary">Global</strong> Resource
                            </h2>
                        </div>
                        <button className="cf-close text-secondary" onClick={onClose}>
                            ✕
                        </button>
                    </div>

                    <div className="cf-body">
                        <label className={errors["Resource Name"] ? "error" : ""}>
                            {getSchemaFieldTitle(schema, "Resource Name", "Resource Name")}
                            <span className="req">*</span>
                            <input
                                className="text"
                                autoFocus
                                value={formData["Resource Name"] || ""}
                                onChange={(e) => {
                                    update("Resource Name", e.target.value);
                                    clearFieldError("Resource Name");
                                }}
                                placeholder="e.g., Projects"
                            />
                        </label>

                        <label className={errors["Resource Description"] ? "error" : ""}>
                            {getSchemaFieldTitle(schema, "Resource Description", "Resource Description")}
                            <textarea
                                className="text"
                                value={formData["Resource Description"] || ""}
                                onChange={(e) => {
                                    update("Resource Description", e.target.value);
                                    clearFieldError("Resource Description");
                                }}
                                placeholder="e.g., Define no. of projects"
                            />
                        </label>

                        <label className={errors[categoryKey] ? "error" : ""}>
                            {prettyFieldLabel(categoryKey, categoryOptions)}
                            <span className="req">*</span>
                            <select
                                value={selectedCategory || ""}
                                className="text"
                                onChange={(e) => {
                                    update(categoryKey, e.target.value);
                                    clearFieldError(categoryKey);
                                }}
                            >
                                {(categoryOptions.enum || []).map((val, i) => (
                                    <option key={val} value={val} className="text">
                                        {categoryOptions.enumNames?.[i] || val}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="cf-grid-2">
                            {showUnit && (
                                <label className={errors["Unit"] ? "error" : ""}>
                                    {getSchemaFieldTitle(schema, "Unit", "Unit")}
                                    <span className="req">*</span>
                                    <select
                                        className="text"
                                        value={formData["Unit"] || ""}
                                        onChange={(e) => {
                                            update("Unit", e.target.value);
                                            clearFieldError("Unit");
                                        }}
                                    >
                                        {unitOptions.enum.map((val, i) => (
                                            <option key={val} value={val} className="text">
                                                {unitOptions.enumNames?.[i] || val}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            <label className={errors["Unit Price"] ? "error" : ""}>
                                {getSchemaFieldTitle(schema, "Unit Price", "Unit Price")}
                                <span className="req">*</span>
                                <input
                                    type="number"
                                    min={0}
                                    className="text"
                                    value={formData["Unit Price"] ?? ""}
                                    onFocus={handleZeroOnFocus}
                                    onChange={(e) => {
                                        update("Unit Price", e.target.value);
                                        clearFieldError("Unit Price");
                                    }}
                                />
                            </label>
                        </div>

                        {specEntries.length > 0 && specEntries.map(([key, fieldSchema]) => {
                            const control = getFieldControl(fieldSchema);
                            return (
                                <label key={key} className={errors[key] ? "error" : ""}>
                                    {prettyFieldLabel(key, fieldSchema)}
                                    {control.kind === "select" ? (
                                        <select
                                            className="text"
                                            value={formData[key] || ""}
                                            onChange={(e) => {
                                                update(key, e.target.value);
                                                clearFieldError(key);
                                            }}
                                        >
                                            {control.enum.map((val, i) => (
                                                <option key={val} value={val} className="text">
                                                    {control.enumNames[i] || val}
                                                </option>
                                            ))}
                                        </select>
                                    ) : control.kind === "boolean" ? (
                                        <input
                                            type="checkbox"
                                            checked={!!formData[key]}
                                            onChange={(e) => {
                                                update(key, e.target.checked);
                                                clearFieldError(key);
                                            }}
                                        />
                                    ) : (
                                        <input
                                            type={control.kind === "number" ? "number" : "text"}
                                            min={control.kind === "number" ? 0 : undefined}
                                            className="text"
                                            value={formData[key] ?? ""}
                                            onFocus={control.kind === "number" ? handleZeroOnFocus : undefined}
                                            onChange={(e) => {
                                                update(key, control.kind === "boolean" ? e.target.checked : e.target.value);
                                                clearFieldError(key);
                                            }}
                                        />
                                    )}
                                </label>
                            );
                        })}

                        <div className="cf-grid-2">
                            <label className={errors["min_value"] ? "error" : ""}>
                                {getSchemaFieldTitle(schema, "min_value", "Min Value")}
                                <input
                                    type="number"
                                    min={0}
                                    className="text"
                                    value={formData.min_value ?? 0}
                                    onFocus={handleZeroOnFocus}
                                    onChange={(e) => {
                                        update("min_value", e.target.value);
                                        clearFieldError("min_value");
                                    }}
                                />
                            </label>

                            <label className={errors["max_value"] ? "error" : ""}>
                                {getSchemaFieldTitle(schema, "max_value", "Max Value")}
                                <input
                                    type="number"
                                    min={0}
                                    className="text"
                                    value={formData.max_value ?? 0}
                                    onFocus={handleZeroOnFocus}
                                    onChange={(e) => {
                                        update("max_value", e.target.value);
                                        clearFieldError("max_value");
                                    }}
                                />
                            </label>
                        </div>
                    </div>

                    {submitError && (
                        <div
                            className={
                                isUpdateBlocked
                                    ? "cf-blocked-message"
                                    : `d-j-a fw-bold mt-3 ${submitError === warningMessage ? "text-warning" : "text-danger"}`
                            }
                            role={isUpdateBlocked ? "alert" : undefined}
                            style={isUpdateBlocked ? undefined : { fontSize: "10px" }}
                        >
                            {submitError}
                        </div>
                    )}

                    <div className="cf-footer">
                        <div className="cf-note"></div>

                        <button
                            className="cf-submit bg-primary"
                            onClick={handleSubmit}
                            disabled={submitLoading || isUpdateBlocked}
                        >
                            {submitLoading
                                ? isEdit
                                    ? "Updating…"
                                    : "Submitting…"
                                : isUpdateBlocked
                                    ? "Cannot Update"
                                    : isEdit
                                    ? "Update Resource"
                                    : "Submit Request"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="mobile-view-bottom"></div>
        </>
    );
}
